import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { ShieldCheck, Clock3, Truck, HeartHandshake, Phone } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const items = [
  {
    icon: Clock3,
    title: "24/7 Availability",
    text: "Real people answering the phone at 3 a.m. or 3 p.m. — every day of the year.",
  },
  {
    icon: Truck,
    title: "Right Equipment",
    text: "Flatbeds for low-clearance and AWD vehicles, wheel-lifts, winches — the right tool for the job.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    text: "Fully insured operators trained to move your vehicle safely.",
  },
  {
    icon: HeartHandshake,
    title: "Honest, Local Team",
    text: "Family-run out of Kirkland. Fair prices, quoted up-front. No shady add-ons.",
  },
];

export default function WhyChooseUs() {
  const leftRef = useReveal();
  const rightRef = useReveal(0.05);
  return (
    <section id="about" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(250,204,21,0.12) 0%, rgba(0,0,0,0) 55%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div ref={leftRef} className="lux-reveal lg:col-span-5 relative">
          <div className="relative">
            <div
              className="aspect-[4/5] w-full bg-cover bg-center rounded-2xl overflow-hidden lux-glass-strong"
              style={{ backgroundImage: "url('/images/job-1.webp')", filter: "brightness(1.05) saturate(1.1)" }}
              role="img"
              aria-label="BM Towing flatbed with a Porsche 911 in Bellevue"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 rounded-b-2xl"
              style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)" }}
            />
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-3 sm:-right-6 lux-glass-strong rounded-2xl px-5 py-4 max-w-[240px]">
              <div className="font-display font-black text-yellow-400 text-4xl leading-none">24/7</div>
              <div className="mt-1 font-display uppercase tracking-[0.22em] text-[10px] text-zinc-300">
                Emergency Tow Line
              </div>
            </div>
          </div>
        </div>

        <div ref={rightRef} className="lux-reveal lux-reveal-delay-2 lg:col-span-7">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
            About BM Towing
          </div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            A local tow team you can <span className="text-yellow-400">actually count on.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            BM Towing &amp; Recovery is a family-run towing company based in Kirkland, WA. We help drivers across the Eastside and Greater Seattle when things go sideways — a dead battery in a parking lot, a flat on the shoulder of I-405, a car stuck in the snow, or a vehicle that needs to be flatbedded across town.
          </p>
          <p className="mt-4 text-zinc-500 text-sm sm:text-base font-light leading-relaxed">
            We keep it simple: pick up the phone fast, give you an honest ETA and a fair price, and treat your vehicle like it's ours.
          </p>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((it, i) => {
              const I = it.icon;
              return (
                <li
                  key={it.title}
                  className={`flex gap-4 lux-card lux-glass rounded-2xl p-5 lux-reveal lux-reveal-delay-${(i % 4) + 1}`}
                >
                  <div className="h-12 w-12 shrink-0 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                    <I className="h-6 w-6 text-yellow-400" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-display font-black uppercase text-white text-lg tracking-tight">
                      {it.title}
                    </div>
                    <div className="text-zinc-400 text-sm mt-1 font-light leading-relaxed">{it.text}</div>
                  </div>
                </li>
              );
            })}
          </ul>

          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="about-call-btn"
            className="mt-10 inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-4 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </section>
  );
}
